import { InputHTMLAttributes, forwardRef } from 'react';

export type InputState = 'default' | 'error' | 'success';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  helperText?: string;
  errorMessage?: string;
  state?: InputState;
  fullWidth?: boolean;
}

const stateStyles: Record<InputState, string> = {
  default: 'border-slate-600 focus:border-purple-500 focus:ring-purple-500/20',
  error: 'border-red-500 focus:border-red-500 focus:ring-red-500/20',
  success: 'border-green-500 focus:border-green-500 focus:ring-green-500/20',
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helperText,
      errorMessage,
      state = 'default',
      fullWidth = false,
      className = '',
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const actualState = errorMessage ? 'error' : state;

    return (
      <div className={`${fullWidth ? 'w-full' : ''}`}>
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-slate-300 mb-1.5"
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          className={`
            block rounded-lg border bg-slate-800/50 px-4 py-2.5
            text-white placeholder-slate-400
            transition-colors duration-200
            focus:outline-none focus:ring-2
            disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-slate-800
            ${stateStyles[actualState]}
            ${fullWidth ? 'w-full' : ''}
            ${className}
          `}
          {...props}
        />
        {(errorMessage || helperText) && (
          <p
            className={`mt-1.5 text-sm ${
              errorMessage ? 'text-red-400' : 'text-slate-400'
            }`}
          >
            {errorMessage || helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
